import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";
import { IconButton, Typography, Box, Button, List, ListItem, ListItemText, ListItemIcon, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Download, FilePresent, InfoOutlined, UploadFile } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { addAttachmentsForTransportOrder, deleteTransportOrderAttachment, downloadAttachmentForTransportOrder, getAttachmentsForTransportOrder } from "../../../services/transportOrderService";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import Grid2 from "@mui/material/Unstable_Grid2";
import DeleteIcon from "@mui/icons-material/Delete";
import { AttachmentListItemDto } from "../../../models/attachment/attachment";
import { LoadingIndicator } from "../../framework/loadingIndicatorNew";
import { AppUtils } from "../../../models/common/appUtils";
import { ConfirmationDialogResult } from "../../../models/common/enums";
import { Alert } from "@mui/material";
import mergeFileLists from "../../../models/common/mergeFileLists";

const fileSizeLimit = 1024000 * 10; // 10 MB limit
const acceptedFileTypes = "image/png, image/jpeg, application/pdf";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

interface ISidebarOrderDetailsProps {
    transportOrder: TransportOrderListItemDto;
    onClose: () => void;
}

export const SidebarOrderAttachments = ({
    transportOrder,
    onClose,
}: ISidebarOrderDetailsProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [fileFormData, setFileFormData] = useState<FormData>(null);
    const [files, setFiles] = useState<FileList>(null);
    const [fileNames, setFileNames] = useState<string[]>(null);
    const [transportOrderFiles, setTransportOrderFiles] = useState<AttachmentListItemDto[]>(null);
    const [hasInvalidFiles, setHasInvalidFiles] = useState(false);

    const fetchOrderFiles = useCallback(async() => {
        setLoading(true);
        try {
            const res = await getAttachmentsForTransportOrder(transportOrder.id);
            if (res?.data) {
                setTransportOrderFiles(res.data);
            }
        } finally {
            setLoading(false);
        }
    }, [transportOrder]);

    useEffect(() => {
        setTransportOrderFiles(transportOrder?.attachments);
    }, [transportOrder]);

    const handleFileAdd = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        setHasInvalidFiles(false);

        const formData = new FormData();
        const newFileNames = [];

        let newFileList = e.target.files;
        if (files?.length > 0) {
            newFileList = mergeFileLists(files, e.target.files);
        }

        let i = 0;
        while (i < newFileList.length) {
            // Check that file size and file type are allowed
            if (newFileList[i].size < fileSizeLimit &&
                acceptedFileTypes.includes(newFileList[i].type)
            ) {
                const file = newFileList[i];
                newFileNames.push(file.name);
                formData.append("files", file);
            } else {
                setHasInvalidFiles(true);
            }
            i++;
        }
        // Set updated files and form data
        setFiles(newFileList);
        setFileNames(newFileNames);
        setFileFormData(formData);
    };

    const handleFileUploadAction = () => {
        setIsUploading(true);
        void handleFileUpload();
    };
    const handleFileUpload = () => {
        addAttachmentsForTransportOrder(fileFormData, transportOrder.id)
            .then(() => {
                void fetchOrderFiles();
                setHasInvalidFiles(false);
                setFiles(null);
                setFileNames(null);
                setFileFormData(null);
            })
            .catch(e => {
                console.log(e);
            })
            .finally(() => {
                setIsUploading(false);
            });
    };

    const handleFilesToUploadDelete = (fileIndex: number) => {
        const updatedFiles = new DataTransfer();
        const formData = new FormData();
        const fileNames = [];
        let i = 0;
        while (i < files.length) {
            if (i !== fileIndex) {
                const file = files[i];
                updatedFiles.items.add(file);
                fileNames.push(file.name);
                formData.append("files", file);
            }
            i++;
        }
        setFiles(updatedFiles.files);
        setFileNames(fileNames);
        setFileFormData(formData);
    };

    const handleFileDownload = (file: AttachmentListItemDto) => {
        downloadAttachmentForTransportOrder(transportOrder.id, file.attachmentId)
            .then(res => {
                const data = new Blob([res.data], { type: file.fileType });
                const fileURL = window.URL.createObjectURL(data);
                const tempLink = document.createElement("a");
                tempLink.href = fileURL;
                tempLink.setAttribute("download", file.fileName);
                tempLink.click();
            })
            .catch(e => {
                console.log(e);
            });
    };

    const handleFileDelete = async(attachmentId: string, fileName: string) => {
        const confirmationResult = await AppUtils.showConfirmationDialog(
            t("transport.order.deleteOrderAttachment", { fileName: fileName })
        );
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;

        void deleteTransportOrderAttachment(transportOrder.id, attachmentId)
            .then(() => {
                void fetchOrderFiles();
            })
            .catch(e => {
                console.log(e);
            });
    };

    return (
        <Stack spacing={2} sx={{ width: "100%" }} fontSize="small" paddingBottom={2}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography variant="h4">{t("common.attachments")}</Typography>
                <Box>
                    <IconButton className="close-panel-button" size="small" onClick={onClose}>
                        <CloseIcon color="primary" />
                    </IconButton>
                </Box>
            </Stack>
            <Grid2>
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {transportOrderFiles && transportOrderFiles.length > 0 ? (
                            <List>
                                {transportOrderFiles.map((file, index) =>
                                    <ListItem
                                        key={index}
                                        disablePadding
                                        secondaryAction={
                                            <>
                                                <IconButton
                                                    onClick={() => void handleFileDownload(file)}
                                                    edge="end"
                                                    aria-label="download"
                                                    sx={{ marginRight: 0 }}
                                                >
                                                    <Download color="primary" />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => void handleFileDelete(file.attachmentId, file.fileName)}
                                                    edge="end"
                                                    aria-label="delete"
                                                >
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </>
                                        }
                                    >
                                        <ListItemIcon style={{ minWidth: "38px" }}>
                                            <FilePresent />
                                        </ListItemIcon>
                                        <ListItemText
                                            disableTypography
                                            primary={
                                                <Tooltip title={file.fileName} placement="bottom">
                                                    <Typography
                                                        variant="body2"
                                                        className="text-truncate"
                                                        style={{ maxWidth: "calc(100% - 90px)" }}
                                                    >
                                                        {file.fileName}
                                                    </Typography>
                                                </Tooltip>
                                            }
                                        />
                                    </ListItem>
                                )}
                            </List>
                        ) : (
                            <Typography variant="body2">
                                {t("transport.order.noAttachments")}
                            </Typography>
                        )}
                    </>
                )}
            </Grid2>
            <Box>
                <Typography
                    color="primary.dark"
                    variant="h4"
                >
                    {t("transport.order.addAttachments")}
                </Typography>
                <Box>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined />
                            </ListItemIcon>
                            <ListItemText
                                primary={t("common.allowedFileFormats", { formats: "JPG, PNG, PDF" })}
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <InfoOutlined />
                            </ListItemIcon>
                            <ListItemText
                                primary={t("common.maxFileSize", { size: "10 MB" })}
                            />
                        </ListItem>
                    </List>
                </Box>
                <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    tabIndex={-1}
                    startIcon={<UploadFile />}
                >
                    {t("common.addAttachments")}
                    <VisuallyHiddenInput
                        accept={acceptedFileTypes}
                        type="file"
                        onChange={handleFileAdd}
                        multiple
                    />
                </Button>
                {fileNames && fileNames.length > 0 && (
                <Box>
                    <Grid2>
                        <List sx={{ paddingY: 2 }}>
                            {fileNames.map((file, index) =>
                                <ListItem
                                    key={index}
                                    disablePadding
                                    secondaryAction={
                                        <IconButton
                                            onClick={() => void handleFilesToUploadDelete(index)}
                                            edge="end"
                                            aria-label="delete"
                                        >
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemIcon>
                                        <FilePresent />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Grid2>
                    <Button
                        color="success"
                        disabled={isUploading}
                        variant="contained"
                        onClick={() => void handleFileUploadAction()}
                    >
                        {t("common.save")}
                    </Button>
                </Box>
                )}
                {hasInvalidFiles && (
                    <Box marginTop={2}>
                        <Grid2>
                            <Alert severity="error" onClose={() => {setHasInvalidFiles(false);}}>
                                {t("common.attachmentFileInvalid")}
                            </Alert>
                        </Grid2>
                    </Box>
                )}
            </Box>
        </Stack>
    );
};
